import { useMutation, useQueryClient } from "@tanstack/react-query"
import { missionsKey } from "../queries/mission-queries"
import { Mission } from "../types/Mission"
import { useMsal } from "@azure/msal-react";
import { BackendAPI } from "../util/Env";

export const useDeleteMissionMutation = () => {
    const queryClient = useQueryClient();
    const { accounts } = useMsal()
    return useMutation({
        mutationFn: async (missionId: string) => {
            const response = await fetch(`${BackendAPI}/missions-api.php`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'ownerId': accounts[0].idTokenClaims!.oid!,
                },
                body: JSON.stringify({ id: missionId }),
            });

            if (!response.ok) {
                throw new Error(`Error deleting mission: ${response.statusText}`);
            }

            return response.json();
        },
        onMutate: async (missionId: string) => {
            await queryClient.cancelQueries({ queryKey: [missionsKey] });

            const prevMissions = queryClient.getQueryData<Mission[]>([missionsKey]);

            queryClient.setQueryData([missionsKey], (old: Mission[] = []) =>
                old.filter((mission) => mission.id !== missionId)
            );

            return { prevMissions };
        },
        onError: (_, _2, context) => {
            queryClient.setQueryData([missionsKey], context?.prevMissions ?? []);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [missionsKey] });
        },
    });
};

export const useCreateMissionMutation = () => {
    const queryClient = useQueryClient();
    const { accounts } = useMsal()
    return useMutation({
        mutationFn: async (newMission: Omit<Mission, 'id'>) => {
            const response = await fetch(`${BackendAPI}/missions-api.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'ownerId': accounts[0].idTokenClaims!.oid!,
                },
                body: JSON.stringify(newMission),
            });

            if (!response.ok) {
                throw new Error(`Error creating mission: ${response.statusText}`);
            }

            return response.json();
        },
        onMutate: async (newMission: Omit<Mission, 'id'>) => {
            await queryClient.cancelQueries({ queryKey: [missionsKey] });

            const prevMissions = queryClient.getQueryData<Mission[]>([missionsKey]);

            const tempMission = {
                ...newMission,
                id: `temp-${Date.now()}`,
            };
            queryClient.setQueryData([missionsKey], (old: Mission[] = []) => [...old, tempMission]);

            return { prevMissions };
        },
        onError: (_, _2, context) => {
            queryClient.setQueryData([missionsKey], context?.prevMissions ?? []);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [missionsKey] });
        },
    });
};